import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './Legal.css';

const RefundPolicy = () => {
  return (
    <>
      <SEO
        title="Refund Policy"
        description="Refund Policy for SilverFox Media. Understand our refund terms for templates, services, and hosting."
        url="/refund-policy"
      />

      {/* Hero Section */}
      <section className="legal-hero">
        <div className="legal-hero__bg">
          <div className="legal-hero__gradient-orb legal-hero__gradient-orb--1" />
          <div className="legal-hero__gradient-orb legal-hero__gradient-orb--2" />
        </div>
        <div className="container">
          <motion.div
            className="legal-hero__content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Refund Policy</h1>
            <p>Last updated: January 15, 2026</p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="legal-content section">
        <div className="container">
          <motion.div
            className="legal-content__wrapper"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="legal-section legal-highlight">
              <h2>Summary</h2>
              <p>
                <strong>Website Templates:</strong> No refunds once the template ZIP file has been downloaded. Due to the digital nature of our products, all template sales are final.
              </p>
            </div>

            <div className="legal-section">
              <h2>1. Website Templates</h2>
              <h3>No Refunds After Download</h3>
              <p>
                All website template purchases are <strong>non-refundable once the template files have been downloaded</strong>. Because our templates are digital products that can be copied instantly, we cannot offer refunds after delivery.
              </p>
              <p>
                By purchasing and downloading a template, you acknowledge and agree that:
              </p>
              <ul>
                <li>You have reviewed the live demo before purchasing</li>
                <li>You understand the template's features and limitations</li>
                <li>You accept that the sale is final upon download</li>
              </ul>

              <h3>Before You Purchase</h3>
              <p>
                We strongly encourage you to:
              </p>
              <ul>
                <li><strong>Preview the live demo:</strong> Every template has a live preview link so you can see exactly what you're getting.</li>
                <li><strong>Review the features list:</strong> Each template lists its included features and pages.</li>
                <li><strong>Contact us with questions:</strong> If you're unsure whether a template is right for your needs, reach out before purchasing.</li>
              </ul>

              <h3>Exceptions</h3>
              <p>
                We may consider a refund or exchange in the following rare circumstances:
              </p>
              <ul>
                <li><strong>Technical issues:</strong> If the template files are corrupted or missing critical components that prevent normal use, and we cannot resolve the issue.</li>
                <li><strong>Duplicate purchase:</strong> If you accidentally purchased the same template twice, contact us immediately.</li>
              </ul>
              <p>
                These exceptions are evaluated on a case-by-case basis and are at our sole discretion.
              </p>
            </div>

            <div className="legal-section">
              <h2>2. Full Setup Services</h2>
              <p>
                For Full Setup services ($499), refund eligibility depends on the project stage:
              </p>
              <ul>
                <li><strong>Before work begins:</strong> Full refund available if you cancel within 24 hours of purchase and before we begin customization work.</li>
                <li><strong>Work in progress:</strong> Partial refund may be available depending on work completed. We will provide an itemized breakdown of completed work.</li>
                <li><strong>After completion:</strong> No refunds once the project has been delivered and deployed.</li>
              </ul>
              <p>
                If you are unsatisfied with the work during the project, please communicate with us immediately so we can address your concerns before completion.
              </p>
            </div>

            <div className="legal-section">
              <h2>3. Custom Development Services</h2>
              <p>
                Custom development projects are governed by individual project agreements. Generally:
              </p>
              <ul>
                <li><strong>Deposits:</strong> Initial deposits are non-refundable as they secure your project slot and cover initial planning work.</li>
                <li><strong>Milestone payments:</strong> Payments for completed milestones are non-refundable.</li>
                <li><strong>Cancellation:</strong> If you cancel a project mid-development, you will be billed for all work completed to date.</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>4. Hosting & Maintenance</h2>
              <p>
                Hosting and maintenance services ($100/month) are billed monthly in advance.
              </p>
              <ul>
                <li><strong>No partial refunds:</strong> We do not offer refunds for partial months of service.</li>
                <li><strong>Cancellation:</strong> You may cancel your hosting at any time. Service will continue until the end of your current billing period.</li>
                <li><strong>Data retention:</strong> Upon cancellation, we will provide your website files and assist with migration upon request.</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>5. Analytics Setup</h2>
              <p>
                Analytics Setup services ($299) are non-refundable once:
              </p>
              <ul>
                <li>We have been granted access to your Google Analytics and Search Console accounts</li>
                <li>Configuration work has begun</li>
              </ul>
            </div>

            <div className="legal-section">
              <h2>6. How to Request a Refund</h2>
              <p>
                If you believe you qualify for a refund under the exceptions listed above, please contact us with:
              </p>
              <ul>
                <li>Your order number or transaction ID</li>
                <li>The email address used for purchase</li>
                <li>A detailed explanation of your refund request</li>
                <li>Any relevant screenshots or documentation</li>
              </ul>
              <p>
                We will review your request and respond within 3-5 business days.
              </p>
            </div>

            <div className="legal-section">
              <h2>7. Chargebacks</h2>
              <p>
                If you initiate a chargeback or payment dispute without first contacting us, we reserve the right to:
              </p>
              <ul>
                <li>Revoke your template license</li>
                <li>Suspend or terminate any active services</li>
                <li>Pursue the disputed amount through legal means</li>
              </ul>
              <p>
                Please contact us directly to resolve any issues before disputing a charge with your bank.
              </p>
            </div>

            <div className="legal-section">
              <h2>8. Contact Us</h2>
              <p>
                For refund requests or questions about this policy, please contact us:
              </p>
              <ul className="legal-contact">
                <li><strong>Email:</strong> james@silverfoxmedia.co</li>
                <li><strong>Phone:</strong> (972) 800-8105</li>
              </ul>
            </div>

          </motion.div>
        </div>
      </section>
    </>
  );
};

export default RefundPolicy;
